/**
 * Feature Toggles for Sankat Mochan Samadhan
 * 
 * This module provides feature flags for gradual migration from the
 * monolithic SankatMochanSamadhan.js to the new modular components.
 * 
 * Usage:
 * - Set toggles via localStorage: localStorage.setItem('SM_USE_NEW_REPORTS', 'true')
 * - Or via URL param: ?feature_reports=true
 * - Admin can toggle via /admin/feature-flags
 */

// Default feature toggle values
const DEFAULT_TOGGLES = {
  USE_NEW_REPORTS_VIEW: false,      // New ReportsView.js component
  USE_NEW_CONSULTATION_VIEW: false, // New ConsultationView.js component
  USE_NEW_PAYMENT_VIEW: false,      // New PaymentView.js component
  USE_NEW_TRIAL_VIEW: false,        // New FreeTrialView.js component
  USE_NEW_HEALTH_VIEW: false,       // New HealthView.js component
  USE_ALL_NEW_COMPONENTS: false,    // Master switch for all new components
};

// LocalStorage keys
const STORAGE_PREFIX = 'SM_FEATURE_';

/**
 * Get a feature toggle value
 * Priority: URL param > localStorage > default
 */
export const getFeatureToggle = (toggleName) => {
  // Check URL params first (for easy testing)
  const urlParams = new URLSearchParams(window.location.search);
  const urlKey = `feature_${toggleName.toLowerCase().replace('use_new_', '').replace('_view', '')}`;
  const urlValue = urlParams.get(urlKey);
  if (urlValue !== null) {
    return urlValue === 'true' || urlValue === '1';
  }
  
  // Check localStorage
  const storageKey = STORAGE_PREFIX + toggleName;
  const storedValue = localStorage.getItem(storageKey);
  if (storedValue !== null) {
    return storedValue === 'true';
  }
  
  // Return default
  return DEFAULT_TOGGLES[toggleName] || false;
};

/**
 * Set a feature toggle value
 */
export const setFeatureToggle = (toggleName, value) => {
  const storageKey = STORAGE_PREFIX + toggleName;
  localStorage.setItem(storageKey, String(value));
  
  // Dispatch event for components to react
  window.dispatchEvent(new CustomEvent('featureToggleChanged', {
    detail: { toggleName, value }
  }));
};

/**
 * Get all feature toggles
 */
export const getAllFeatureToggles = () => {
  const toggles = {};
  Object.keys(DEFAULT_TOGGLES).forEach(key => {
    toggles[key] = getFeatureToggle(key);
  });
  return toggles;
};

/**
 * Reset all feature toggles to defaults
 */
export const resetFeatureToggles = () => {
  Object.keys(DEFAULT_TOGGLES).forEach(key => {
    localStorage.removeItem(STORAGE_PREFIX + key);
  });
};

/**
 * Enable all new components (for testing)
 */
export const enableAllNewComponents = () => {
  Object.keys(DEFAULT_TOGGLES).forEach(key => {
    setFeatureToggle(key, true);
  });
};

/**
 * Disable all new components (rollback)
 */
export const disableAllNewComponents = () => {
  Object.keys(DEFAULT_TOGGLES).forEach(key => {
    setFeatureToggle(key, false);
  });
};

/**
 * Hook to use feature toggles in React components
 */
import { useState, useEffect } from 'react';

export const useFeatureToggle = (toggleName) => {
  const [enabled, setEnabled] = useState(() => getFeatureToggle(toggleName));
  
  useEffect(() => {
    const handleChange = (event) => {
      if (event.detail.toggleName === toggleName) {
        setEnabled(event.detail.value);
      }
    };
    
    window.addEventListener('featureToggleChanged', handleChange);
    return () => window.removeEventListener('featureToggleChanged', handleChange);
  }, [toggleName]);
  
  return enabled;
};

/**
 * Hook to use all feature toggles
 */
export const useAllFeatureToggles = () => {
  const [toggles, setToggles] = useState(() => getAllFeatureToggles());
  
  useEffect(() => {
    const handleChange = () => {
      setToggles(getAllFeatureToggles());
    };
    
    window.addEventListener('featureToggleChanged', handleChange);
    return () => window.removeEventListener('featureToggleChanged', handleChange);
  }, []);
  
  return toggles;
};

// Export toggle names for type safety
export const FEATURE_TOGGLES = {
  USE_NEW_REPORTS_VIEW: 'USE_NEW_REPORTS_VIEW',
  USE_NEW_CONSULTATION_VIEW: 'USE_NEW_CONSULTATION_VIEW',
  USE_NEW_PAYMENT_VIEW: 'USE_NEW_PAYMENT_VIEW',
  USE_NEW_TRIAL_VIEW: 'USE_NEW_TRIAL_VIEW',
  USE_NEW_HEALTH_VIEW: 'USE_NEW_HEALTH_VIEW',
  USE_ALL_NEW_COMPONENTS: 'USE_ALL_NEW_COMPONENTS',
};

export default {
  getFeatureToggle,
  setFeatureToggle,
  getAllFeatureToggles,
  resetFeatureToggles,
  enableAllNewComponents,
  disableAllNewComponents,
  useFeatureToggle,
  useAllFeatureToggles,
  FEATURE_TOGGLES,
};
